import {
  Divider,
  Box,
  List,
  ListItem,
  ListItemText,
  Typography,
  useTheme,
  alpha,
} from '@mui/material'
import React from 'react'
import { useNavigate } from 'react-router-dom'

const determineRoute = (item: any) => {
  if (item?.SK?.startsWith('PRODUCT_')) return
  if (item?.SK?.startsWith('NOTIFY_')) return
  if (item?.SK?.startsWith?.('CREATION_TIMESTAMP_')) return `/organizations/${item?.PK}/details`
  if (item?.SK?.startsWith?.('DEAL_')) return `/deals/${item?.PK}/${item?.SK}/details`
  if (item?.contacts_uuid) return `/contacts/${item?.contacts_uuid}/details`
}

const CompactList = ({
  title,
  filter,
  items,
  primary,
  secondary,
  maxHeight = 320,
}: {
  title: string | React.ReactNode
  filter?: React.ReactNode
  items: any[]
  primary: (item: any) => React.ReactNode
  secondary?: (item: any) => React.ReactNode
  maxHeight?: number
}) => {
  const theme = useTheme()
  const isDark = theme.palette.mode === 'dark'
  const navigate = useNavigate()

  return (
    <Box
      sx={{
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        bgcolor: alpha(theme.palette.text.primary, isDark ? 0.02 : 0.01),
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 1.5,
          py: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: alpha(theme.palette.text.primary, isDark ? 0.03 : 0.02),
        }}
      >
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          {filter}
        </Box>
        {title}
      </Box>

      {/* List */}
      <Box sx={{ maxHeight, overflowY: 'auto', flex: 1 }}>
        <List dense disablePadding sx={{ overflowX: 'hidden' }}>
          {items.length ? (
            items.map((it, idx) => {
              const route = determineRoute(it)
              return (
                <React.Fragment
                  key={it.SK && it.PK ? `${it.PK}-${it.SK}` : it.deal_uuid || it.product_uuid || it.org_uuid || idx}
                >
                  <ListItem
                    sx={{
                      px: 1.5,
                      py: 0.5,
                      borderRadius: 1,
                      mx: 0.5,
                      transition: 'background-color 0.15s ease',
                      '&:hover': {
                        bgcolor: alpha(theme.palette.text.primary, isDark ? 0.04 : 0.03),
                      },
                      cursor: route ? 'pointer' : 'default',
                    }}
                    onClick={() => (route ? navigate(route) : null)}
                  >
                    <ListItemText
                      primaryTypographyProps={{
                        noWrap: true,
                        sx: {
                          fontWeight: 600,
                          fontSize: 13,
                          color: 'text.primary',
                        },
                      }}
                      secondaryTypographyProps={{
                        component: 'div',
                        sx: {
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          fontSize: 11,
                          color: 'text.secondary',
                        },
                      }}
                      primary={primary(it)}
                      secondary={secondary ? secondary(it) : undefined}
                    />
                  </ListItem>
                  {idx < items.length - 1 && (
                    <Divider component="li" sx={{ my: 0.25, mx: 1.5, borderColor: 'divider' }} />
                  )}
                </React.Fragment>
              )
            })
          ) : (
            <ListItem sx={{ px: 1.5 }}>
              <ListItemText
                primary="No items"
                primaryTypographyProps={{ sx: { fontSize: 12, color: 'text.disabled' } }}
              />
            </ListItem>
          )}
        </List>
      </Box>
    </Box>
  )
}
export default CompactList
